/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

// --- CONSTANTES CONFIGURABLES ---
const SAMPLE_RATE_HZ = 24000; // Frecuencia de muestreo en Hz (24kHz como espera Azure)
const BUFFER_SIZE = 4800; // Tamaño del buffer de audio (muestras * 2 bytes por muestra)

// --- URL de los Worklets ---
const InWorkletUrl = '/InAudioProcessor.worklet.js';
const CaptureWorkletUrl = '/OutAudioProcessor.worklet.js'; // Nuevo worklet para captura

// --- Interfaces (mantenidas del original)
interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
}
interface ConversationMode {
  mode: 'speaking' | 'listening';
}
interface ConversationStatus {
  status: 'connected' | 'disconnected' | 'connecting';
}
interface WsAudioMessage {
  type: 'audio';
  audio_event: { audio_base_64: string; event_id?: number; is_end?: boolean };
  message_id?: string;
}
interface WsUserTranscriptMessage {
  type: 'user_transcript';
  user_transcription_event: { user_transcript: string };
}
interface WsAgentResponseMessage {
  type: 'agent_response';
  agent_response_event: { agent_response: string };
  message_id?: string;
}
interface WsConversationMetadataMessage {
  type: 'conversation_initiation_metadata';
  conversation_initiation_metadata_event: {
    conversation_id: string;
    agent_output_audio_format?: string;
    language?: string;
    voice?: string;
    tools?: Array<{
      name: string;
      description: string;
      parameters: Record<string, any>;
    }>;
    agentaiadapters?: {
      sttadapter: string;
      llmadapter: string;
      ttsadapter: string;
    };
  };
}
interface WsErrorMessage {
  type: 'error';
  error: { title: string; message: string };
}
interface WsClientInitiationData {
  type: 'conversation_initiation_client_data';
  conversation_config_override?: {
    agent?: {
      prompt?: {
        prompt?: string;
      };
      first_message?: string;
      language?: string;
      tools?: Array<{
        name: string;
        description: string;
        parameters: Record<string, any>;
      }>;
      agentaiadapters?: {
        sttadapter: string;
        llmadapter: string;
        ttsadapter: string;
      };
    };
    tts?: {
      voice_id?: string;
    };
  };
  custom_llm_extra_body?: any;
}
interface CustomHookProps {
  onConnect?: (data: { conversationId: string }) => void;
  onDisconnect?: () => void;
  onError?: (message: string, context?: any) => void;
  onMessage?: (message: ConversationMessage) => void;
  onModeChange?: (mode: ConversationMode) => void;
  onStatusChange?: (status: ConversationStatus) => void;
  clientTools?: Record<
    string,
    (
      parameters: any
    ) => Promise<string | number | void> | string | number | void
  >;
  onUnhandledClientToolCall?: (params: any) => void;
}
interface CustomStartSessionOptions {
  signedUrl: string;
  overrides?: any;
}
interface WsClientToolCallMessage {
  type: 'client_tool_call';
  client_tool_call: {
    tool_name: string;
    tool_call_id: string;
    parameters: any;
    expects_response: boolean;
  };
}
interface WsClientToolResultMessage {
  type: 'client_tool_result';
  tool_call_id: string;
  result: any;
  is_error: boolean;
}

// Función auxiliar para decodificar Base64 a ArrayBuffer
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  try {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (e) {
    console.error('Error decodificando Base64:', e);
    return new ArrayBuffer(0);
  }
}

export function websocketVirtualAgent({
  onConnect,
  onDisconnect,
  onError,
  onMessage,
  onModeChange,
  onStatusChange,
  clientTools = {},
  onUnhandledClientToolCall
}: CustomHookProps) {
  // --- Estados ---
  const [status, setStatus] =
    useState<ConversationStatus['status']>('disconnected');
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // --- Refs ---
  const wsRef = useRef<WebSocket | null>(null);

  // Refs para Web Audio API
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const isCapturingRef = useRef<boolean>(false);

  // Output refs para reproducción
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const outputWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const outputGainNodeRef = useRef<GainNode | null>(null);

  // --- Callbacks ---
  const updateStatus = useCallback(
    (newStatus: ConversationStatus['status']) => {
      setStatus(newStatus);
      onStatusChange?.({ status: newStatus });
    },
    [onStatusChange]
  );

  // Para estado del agente (manejado por worklet de salida)
  const handlePlaybackWorkletMessage = useCallback(
    ({ data }: MessageEvent) => {
      if (data.type === 'process') {
        const agentIsSpeaking = !data.finished;
        setIsSpeaking(agentIsSpeaking);
        onModeChange?.({ mode: agentIsSpeaking ? 'speaking' : 'listening' });
      }
    },
    [onModeChange]
  );

  // Lógica de captura de audio con AudioWorklet
  const startAudioCapture = useCallback(async () => {
    try {
      // Detener cualquier captura anterior
      stopAudioCapture();

      // Solicitar acceso al micrófono con configuración optimizada
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          channelCount: 1 // Mono para mejor reconocimiento de voz
        }
      });

      // Crear contexto de audio con tasa de muestreo configurada
      audioContextRef.current = new AudioContext({
        sampleRate: SAMPLE_RATE_HZ
      });

      console.log(
        `AudioContext creado con frecuencia: ${audioContextRef.current.sampleRate}Hz`
      );

      // Cargar y registrar el AudioWorklet
      await audioContextRef.current.audioWorklet.addModule(CaptureWorkletUrl);

      // Crear nodo fuente de audio desde micrófono
      streamSourceRef.current = audioContextRef.current.createMediaStreamSource(
        mediaStreamRef.current
      );

      // Crear AudioWorkletNode para procesar audio
      audioWorkletNodeRef.current = new AudioWorkletNode(
        audioContextRef.current,
        'audio-capture-processor',
        {
          // Pasar BUFFER_SIZE como parte de las opciones
          processorOptions: {
            blockSize: BUFFER_SIZE
          },
          // Configuración de número de canales (entrada y salida)
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1] // 1 canal de salida (mono)
        }
      );

      // Inicializar el worklet con la configuración
      audioWorkletNodeRef.current.port.postMessage({
        type: 'init',
        blockSize: BUFFER_SIZE
      });

      // Configurar manejo de mensajes del AudioWorklet
      audioWorkletNodeRef.current.port.onmessage = event => {
        if (
          !isCapturingRef.current ||
          !wsRef.current ||
          wsRef.current.readyState !== WebSocket.OPEN
        ) {
          return;
        }

        if (event.data.type === 'pcm-data') {
          // Obtener el buffer PCM del mensaje
          const pcmBuffer = event.data.pcmData;

          // Enviar datos PCM al servidor
          wsRef.current.send(pcmBuffer);
        }
      };

      // Conectar nodos
      streamSourceRef.current.connect(audioWorkletNodeRef.current);
      audioWorkletNodeRef.current.connect(audioContextRef.current.destination);

      // Marcar como activo
      isCapturingRef.current = true;

      // Informar al worklet que inicie la captura
      audioWorkletNodeRef.current.port.postMessage({ type: 'start' });

      console.log(
        'Captura de audio PCM iniciada correctamente con AudioWorklet'
      );
    } catch (error) {
      console.error('Error iniciando captura de audio:', error);
      onError?.('Error starting audio capture', error);
      stopAudioCapture();
    }
  }, [onError]);

  const stopAudioCapture = useCallback(() => {
    // Detener procesamiento
    isCapturingRef.current = false;

    // Informar al worklet que detenga la captura
    if (audioWorkletNodeRef.current) {
      try {
        audioWorkletNodeRef.current.port.postMessage({ type: 'stop' });
        audioWorkletNodeRef.current.disconnect();
      } catch (e) {
        console.error('Error deteniendo AudioWorkletNode:', e);
      }
      audioWorkletNodeRef.current = null;
    }

    // Desconectar fuente de audio
    if (streamSourceRef.current) {
      try {
        streamSourceRef.current.disconnect();
      } catch (e) {
        console.error('Error desconectando MediaStreamSource:', e);
      }
      streamSourceRef.current = null;
    }

    // Cerrar contexto de audio
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        console.error('Error cerrando AudioContext:', e);
      }
      audioContextRef.current = null;
    }

    // Detener todos los tracks de audio
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  // --- Lógica de Playback
  const initializeOutput = useCallback(async () => {
    if (outputAudioContextRef.current) return;
    try {
      const context = new (window.AudioContext ||
        (window as any).webkitAudioContext)({ sampleRate: 16000 });
      outputAudioContextRef.current = context;
      await context.audioWorklet.addModule(InWorkletUrl);
      const workletNode = new AudioWorkletNode(
        context,
        'audio-concat-processor'
      );
      const gainNode = context.createGain();
      gainNode.gain.value = 1.0;
      workletNode.connect(gainNode);
      gainNode.connect(context.destination);
      outputWorkletNodeRef.current = workletNode;
      outputGainNodeRef.current = gainNode;
      workletNode.port.onmessage = handlePlaybackWorkletMessage;
    } catch (error) {
      console.error('Error inicializando salida de audio:', error);
      onError?.('Failed to initialize audio output', error);
    }
  }, [onError, handlePlaybackWorkletMessage]);

  const stopOutput = useCallback(() => {
    outputWorkletNodeRef.current?.port.close();
    outputWorkletNodeRef.current?.disconnect();
    outputGainNodeRef.current?.disconnect();
    if (outputAudioContextRef.current?.state !== 'closed') {
      outputAudioContextRef.current
        ?.close()
        .catch(e => console.error('Error closing output AC:', e));
    }
    outputWorkletNodeRef.current = null;
    outputGainNodeRef.current = null;
    outputAudioContextRef.current = null;
    setIsSpeaking(false);
  }, []);

  const playAudioChunk = useCallback((audioBuffer: ArrayBuffer) => {
    if (!outputWorkletNodeRef.current || !outputAudioContextRef.current) return;
    if (outputAudioContextRef.current.state === 'suspended') {
      outputAudioContextRef.current
        .resume()
        .catch(e => console.error('Error resuming output context', e));
    }
    try {
      outputWorkletNodeRef.current.port.postMessage(
        { type: 'buffer', buffer: audioBuffer },
        [audioBuffer]
      );
    } catch (e) {
      console.error(
        'Error enviando buffer al worklet de salida (puerto cerrado?):',
        e
      );
    }
  }, []);

  // --- WebSocket Logic (adaptada para datos binarios) ---
  const startSession = useCallback(
    async (options: CustomStartSessionOptions): Promise<string> => {
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      )
        return conversationId ?? '';
      updateStatus('connecting');
      await initializeOutput();

      return new Promise<string>((resolve, reject) => {
        let resolved = false;

        // Crear WebSocket y configurar para datos binarios
        wsRef.current = new WebSocket(options.signedUrl);
        const ws = wsRef.current;
        ws.binaryType = 'arraybuffer'; // Configurar para recibir/enviar datos binarios

        ws.onopen = () => {
          //console.log('WebSocket conectado');

          // Agregar un pequeño delay para asegurar que la conexión está completamente establecida
          if (options.overrides) {
            setTimeout(() => {
              const initMessage: WsClientInitiationData = {
                type: 'conversation_initiation_client_data',
                conversation_config_override: {
                  agent: {
                    prompt: options.overrides.agent?.prompt,
                    first_message: options.overrides.agent?.firstMessage,
                    language: options.overrides.agent?.language,
                    tools: options.overrides.agent?.tools,
                    agentaiadapters: options.overrides.agent?.agentaiadapters
                  },
                  tts: {
                    voice_id: options.overrides.tts?.voiceId
                  }
                },
                custom_llm_extra_body: options.overrides.llm
              };

              try {
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify(initMessage));
                  // console.log('Configuración enviada exitosamente');
                } else {
                  console.error(
                    'WebSocket no está en estado OPEN al intentar enviar la configuración'
                  );
                  reject(new Error('WebSocket not ready for config'));
                  return;
                }
              } catch (error) {
                console.error('Error enviando configuración:', error);
                reject(error);
                return;
              }
            }, 150);
          }

          // Esperar a que se envíe la configuración antes de iniciar la captura de audio
          setTimeout(() => {
            startAudioCapture();
          }, 300);
        };

        // Procesar mensajes del servidor - adaptado para datos binarios
        ws.onmessage = event => {
          try {
            // Comprobar si los datos son texto (JSON) o binarios
            if (typeof event.data === 'string') {
              // Procesar mensaje JSON como antes
              const message = JSON.parse(event.data as string);
              switch (message.type) {
                case 'conversation_initiation_metadata': {
                  const metadataMsg = message as WsConversationMetadataMessage;
                  const metadata =
                    metadataMsg.conversation_initiation_metadata_event;
                  const convId = metadata.conversation_id;

                  setConversationId(convId);
                  updateStatus('connected');
                  onConnect?.({ conversationId: convId });
                  if (!resolved) {
                    resolve(convId);
                    resolved = true;
                  }
                  break;
                }
                case 'audio': {
                  const audioMsg = message as WsAudioMessage;
                  if (audioMsg.audio_event.audio_base_64) {
                    const audioBuffer = base64ToArrayBuffer(
                      audioMsg.audio_event.audio_base_64
                    );
                    if (audioBuffer.byteLength > 0) playAudioChunk(audioBuffer);
                  }
                  break;
                }
                case 'user_transcript':
                  onMessage?.({
                    source: 'user',
                    message: (message as WsUserTranscriptMessage)
                      .user_transcription_event.user_transcript
                  });
                  break;
                case 'agent_response':
                  onMessage?.({
                    source: 'ai',
                    message: (message as WsAgentResponseMessage)
                      .agent_response_event.agent_response
                  });
                  break;
                case 'error': {
                  const errorMsg = message as WsErrorMessage;
                  console.error('Error WS:', errorMsg.error.message);
                  onError?.(errorMsg.error.message, errorMsg.error);
                  break;
                }
                case 'client_tool_call': {
                  const toolCallMsg = message as WsClientToolCallMessage;
                  const { tool_name, tool_call_id, parameters } =
                    toolCallMsg.client_tool_call;

                  if (
                    Object.prototype.hasOwnProperty.call(clientTools, tool_name)
                  ) {
                    try {
                      Promise.resolve(clientTools[tool_name](parameters))
                        .then(result => {
                          const responseMsg: WsClientToolResultMessage = {
                            type: 'client_tool_result',
                            tool_call_id,
                            result:
                              result ?? 'Client tool execution successful.',
                            is_error: false
                          };

                          ws.send(JSON.stringify(responseMsg));
                        })
                        .catch(error => {
                          const errorMsg =
                            error instanceof Error
                              ? error.message
                              : 'Unknown error';

                          console.error(
                            `[Hook] Error ejecutando herramienta ${tool_name}:`,
                            errorMsg
                          );

                          const responseMsg: WsClientToolResultMessage = {
                            type: 'client_tool_result',
                            tool_call_id,
                            result: `Client tool execution failed: ${errorMsg}`,
                            is_error: true
                          };

                          ws.send(JSON.stringify(responseMsg));
                          onError?.(
                            `Client tool execution failed: ${errorMsg}`,
                            {
                              clientToolName: tool_name
                            }
                          );
                        });
                    } catch (e) {
                      const errorMsg =
                        e instanceof Error ? e.message : 'Unknown error';

                      console.error(
                        `[Hook] Error procesando herramienta ${tool_name}:`,
                        errorMsg
                      );

                      const responseMsg: WsClientToolResultMessage = {
                        type: 'client_tool_result',
                        tool_call_id,
                        result: `Client tool execution failed: ${errorMsg}`,
                        is_error: true
                      };

                      ws.send(JSON.stringify(responseMsg));
                      onError?.(`Client tool execution failed: ${errorMsg}`, {
                        clientToolName: tool_name
                      });
                    }
                  } else if (onUnhandledClientToolCall) {
                    onUnhandledClientToolCall(toolCallMsg.client_tool_call);
                  } else {
                    console.warn(
                      `[Hook] Herramienta no implementada: ${tool_name}`
                    );

                    const responseMsg: WsClientToolResultMessage = {
                      type: 'client_tool_result',
                      tool_call_id,
                      result: `Client tool with name ${tool_name} is not defined on client`,
                      is_error: true
                    };

                    ws.send(JSON.stringify(responseMsg));
                    onError?.(
                      `Client tool with name ${tool_name} is not defined on client`,
                      {
                        clientToolName: tool_name
                      }
                    );
                  }
                  break;
                }
                default:
                  console.log(
                    `[Hook] Evento no manejado: ${message.type}`,
                    message
                  );
                  break;
              }
            } else if (event.data instanceof ArrayBuffer) {
              // Manejar datos binarios (audio para reproducción)
              playAudioChunk(event.data);
            }
          } catch (e) {
            console.log(`Error procesando mensaje WebSocket: ${e}`, event.data);
          }
        };

        ws.onerror = event => {
          console.error('WebSocket error:', event);
          updateStatus('disconnected');
          onError?.('WebSocket connection error', event);
          stopAudioCapture();
          stopOutput();
          if (!resolved) {
            reject(new Error('WebSocket connection error'));
            resolved = true;
          }
        };

        ws.onclose = event => {
          console.log('WebSocket desconectado:', event.code, event.reason);
          updateStatus('disconnected');
          onDisconnect?.();
          setConversationId(null);
          stopAudioCapture();
          stopOutput();
          if (!resolved) {
            if (event.code !== 1000 && event.code !== 1005) {
              reject(
                new Error(`WebSocket closed before connecting: ${event.code}`)
              );
            }
            resolved = true;
          }
        };
      });
    },
    [
      updateStatus,
      initializeOutput,
      startAudioCapture,
      stopAudioCapture,
      stopOutput,
      onConnect,
      onMessage,
      onError,
      onDisconnect,
      conversationId,
      playAudioChunk,
      clientTools,
      onUnhandledClientToolCall
    ]
  );

  const endSession = useCallback(async (): Promise<void> => {
    wsRef.current?.close(1000, 'User ended session');
    wsRef.current = null;
    setConversationId(null);
    stopAudioCapture();
    stopOutput();
  }, [stopAudioCapture, stopOutput]);

  useEffect(() => {
    // Limpieza al desmontar
    return () => {
      endSession();
    };
  }, [endSession]);

  // Memoizar valor de retorno
  const returnedValue = useMemo(
    () => ({
      status,
      isSpeaking,
      conversationId,
      startSession,
      endSession
    }),
    [status, isSpeaking, conversationId, startSession, endSession]
  );

  return returnedValue;
}
